import type { FC } from 'react';

import type { CmsEditingContext } from '@eneco/cms-adapter';
import { VisualEditing as SanityVisualEditing } from '@sanity/visual-editing/next-pages-router';

import { DropFeedback } from './DropFeedback';
import { StudioSync } from './StudioSync';

/**
 * Sanity's overlay runtime, mounted by the render host when — and only when — the page
 * came back from the drafts perspective (`CmsContext.editing`).
 *
 * No `next/dynamic` wrapper: the package already lazy-loads its own implementation behind
 * a `Suspense` boundary, and that implementation renders `null` and does everything in an
 * effect, so a server render costs nothing and the browser only pays for the chunk once
 * an editor is actually in draft mode.
 *
 * The `next-pages-router` entry point is the current, maintained one. It wires
 * `next/router` into Presentation's navigation in both directions, and implements the
 * refresh Presentation asks for after a save as `router.replace(asPath, {shallow: false})`,
 * which re-runs `getServerSideProps` and re-fetches drafts — that round-trip is what keeps
 * the preview current without a second data-fetching stack. It also reads `basePath` and
 * `locale` off the router, which is what makes it correct for the containers that set one
 * and for `nl-eneco-main`'s three locales.
 */
export interface VisualEditingProps {
  editing: CmsEditingContext;
}

/** The Studio's origin, or undefined if the configured URL is not one — which must not take the page down. */
const originOf = (studioUrl: string): string | undefined => {
  try {
    return new URL(studioUrl).origin;
  } catch {
    return undefined;
  }
};

export const VisualEditing: FC<VisualEditingProps> = ({ editing }) => {
  const studioOrigin = originOf(editing.studioUrl);

  return (
    <>
      <SanityVisualEditing />
      <DropFeedback />
      {studioOrigin && <StudioSync studioOrigin={studioOrigin} />}
      {/* eslint-disable-next-line dxp-rules/no-inline-css -- editor chrome, not page content: it renders only in draft mode, sits above arbitrary page markup, and must stay legible and positioned whatever is underneath. Routing it through Sparky would put a design-system component on a surface no visitor ever sees. */}
      <a
        href={editing.disableDraftUrl}
        style={{
          position: 'fixed',
          bottom: '1rem',
          right: '1rem',
          zIndex: 999999,
          padding: '0.5rem 0.75rem',
          borderRadius: '0.25rem',
          background: '#1f2937',
          color: '#fff',
          font: '500 0.8125rem/1 system-ui, sans-serif',
          textDecoration: 'none',
        }}>
        Leave draft mode
      </a>
    </>
  );
};
